"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/problem-list.module.css";
import {
  getConsultationFull,
  getPatientConsultationTimeline,
} from "../../../../api/clinic/consultationApi.js";

export default function ProblemList() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const [saving, setSaving] = useState(false);

  const draftKey = "problemList";
  
  const defaultFormData = {
    newProblem: "",
    problems: [],
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const addProblem = () => {
    if (formData.newProblem.trim()) {
      const newProblem = {
        id: Date.now(),
        name: formData.newProblem.trim(),
        date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
        status: "active",
        color: "blue" // Default color for new problems
      };
      
      setFormData((prev) => {
        const next = {
          ...prev,
          problems: [...prev.problems, newProblem],
          newProblem: "",
        };
        setStepDraft(draftKey, next);
        return next;
      });
    }
  };

  const removeProblem = (id) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        problems: prev.problems.filter((problem) => problem.id !== id),
      };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/diagnosis-icd10");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const patientId = consultationData?.patientId;
        const currentConsultationId = consultationData?.consultationId;

        let items = [];

        if (patientId) {
          const timeline = await getPatientConsultationTimeline(patientId);
          if (cancelled) return;

          const rows = Array.isArray(timeline) ? timeline : [];
          const prior = rows.find((c) => String(c?.consultationid ?? c?.consultationId ?? '') !== String(currentConsultationId ?? ''));
          const fromTimeline = prior?.consultation_problem;
          if (Array.isArray(fromTimeline) && fromTimeline.length) {
            items = fromTimeline;
          }
        }

        if ((!items || items.length === 0) && currentConsultationId) {
          const full = await getConsultationFull(currentConsultationId);
          if (cancelled) return;
          const fromFull = full?.consultation_problem;
          if (Array.isArray(fromFull) && fromFull.length) {
            items = fromFull;
          }
        }

        const next = {
          ...defaultFormData,
          problems: items.map((p) => ({
            id: p.id,
            name: p.name,
            date: p.problemdate ? new Date(p.problemdate).toISOString().split("T")[0] : "",
            status: p.status || "",
            color: p.color || "",
          })),
        };

        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId, consultationData?.patientId]);

  const handleNext = async () => {
    try {
      setSaving(true);
      nextStep();
      navigate("/customer/clinic/treatment-plan");
    } finally {
      setSaving(false);
    }
  };

  const getColorClass = (color) => {
    switch(color) {
      case 'orange': return styles.orangeDot;
      case 'yellow': return styles.yellowDot;
      case 'blue': return styles.blueDot;
      default: return styles.defaultDot;
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={7}
            totalSteps={10}
            stepTitle="Problem List"
            stepDetails="Step 7 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* ACTIVE PROBLEM LIST */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>ACTIVE PROBLEM LIST</h3>
              
              {/* Add New Problem */}
              <div className={styles.addProblemContainer}>
                <input
                  type="text"
                  name="newProblem"
                  value={formData.newProblem}
                  onChange={handleInputChange}
                  placeholder="Add a new problem..."
                  className={styles.problemInput}
                  onKeyPress={(e) => e.key === 'Enter' && addProblem()}
                />
                <button
                  type="button"
                  className={styles.addButton}
                  onClick={addProblem}
                >
                  + Add
                </button>
              </div>

              {/* Problems List */}
              <div className={styles.problemsList}>
                {formData.problems.length === 0 ? (
                  <p className={styles.noProblems}>No problems added yet</p>
                ) : (
                  formData.problems.map((problem) => (
                    <div key={problem.id} className={styles.problemItem}>
                      <div className={styles.problemContent}>
                        <div className={styles.problemHeader}>
                          <div className={`${styles.statusDot} ${getColorClass(problem.color)}`}></div>
                          <span className={styles.problemName}>{problem.name}</span>
                        </div>
                        <span className={styles.problemDate}>{problem.date}</span>
                      </div>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeProblem(problem.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
